import {
  fieldWorkspaceCommandSchema,
  fieldWorkspaceJobSchema,
  fieldWorkspaceStatusSchema,
} from '@mio/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiRequestError } from '@/shared/api/api-error';
import { apiRequest } from '@/shared/api/client';
import { workspaceQuery } from '@/shared/api/queries/workspace';
import { editorSessionFor, fieldEditorContextKey } from '@/shared/workspace/editor-sessions';

import type { FieldWorkspaceStatus } from '@mio/shared';

/** Editor-session outcomes that mean "this tab's session cannot certify the Field buffers". */
const EDITOR_SESSION_PAUSE_FAILURES = new Set([
  'editor_disconnected',
  'editor_context_changed',
  'editor_session_changed',
  'editor_in_use',
  'editor_unavailable',
  'editor_capture_interrupted',
]);
const TRANSIENT_STATES = new Set(['queued', 'requested', 'provisioning', 'starting', 'stopping']);
const SETTLE_WAIT_MS = 3 * 60_000;
const OPERATION_WAIT_MS = 5 * 60_000;
/** A disk pause is refused while another browser holds the Field editor; absence proves itself within minutes. */
const ABSENCE_RETRY_MS = 20_000;
const ABSENCE_WAIT_MS = 4 * 60_000;
export const WORKSHOP_SWITCH_MUTATION_KEY = ['workshop', 'field-release'] as const;
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const fieldStatus = () => apiRequest('/field/workspace', fieldWorkspaceStatusSchema);
const busy = (status: FieldWorkspaceStatus) =>
  TRANSIENT_STATES.has(status.state) ||
  Boolean(status.operation && ['pending', 'running'].includes(status.operation.status));

async function waitForFieldOperation(id: string) {
  const deadline = Date.now() + OPERATION_WAIT_MS;
  let failures = 0;
  for (;;) {
    await sleep(1500);
    try {
      const operation = await apiRequest(
        `/field/workspace/operations/${id}`,
        fieldWorkspaceJobSchema,
      );
      failures = 0;
      if (['completed', 'failed'].includes(operation.status)) return operation;
    } catch (error) {
      // One lost poll must not report a running save as a failure.
      if (++failures >= 3) throw error;
    }
    if (Date.now() >= deadline)
      throw new Error(
        'Pausing Workbench is taking longer than usual. It continues in the background; try again in a moment.',
      );
  }
}

/**
 * The Workshop shares the learner's one coding environment with Field. Opening a workshop
 * while Field holds that environment first pauses Field on the same running compute: its
 * project is checkpointed and released without stopping the environment, so the workshop
 * opens in seconds. The Field page stays mounted during app navigation, so its editor is
 * usually still connected in this tab and the pause flushes unsaved buffers through that
 * session; otherwise the files already saved on disk are checkpointed, which the server
 * refuses only while a Field editor is still open elsewhere. The caller activates the
 * workshop once this resolves.
 */
export function useWorkshopWorkspaceSwitch() {
  const client = useQueryClient();
  return useMutation({
    mutationKey: WORKSHOP_SWITCH_MUTATION_KEY,
    mutationFn: async () => {
      let status = await fieldStatus();
      const settleBy = Date.now() + SETTLE_WAIT_MS;
      while (busy(status)) {
        if (Date.now() > settleBy)
          throw new Error('Workbench is still saving or starting. Try again in a minute.');
        await sleep(2500);
        status = await fieldStatus();
      }
      if (status.selection !== 'field' || !status.context) return { released: false };
      if (['stopped', 'failed'].includes(status.state)) return { released: false };
      if (status.state !== 'ready')
        throw new Error('Wait for Workbench to finish starting or stopping, then try again.');
      const context = status.context;
      const pause = async (editorSessionId: string | null) =>
        waitForFieldOperation(
          (
            await apiRequest('/field/workspace/commands', fieldWorkspaceJobSchema, {
              method: 'POST',
              body: fieldWorkspaceCommandSchema.parse({
                requestId: crypto.randomUUID(),
                action: 'stop',
                expectedGeneration: status.generation,
                editorSessionId,
                keepCompute: true,
                ...(editorSessionId ? {} : { saveMode: 'disk' }),
              }),
            })
          ).id,
        );
      let paused = await pause(await editorSessionFor(fieldEditorContextKey(context)));
      if (
        paused.status === 'failed' &&
        paused.error &&
        EDITOR_SESSION_PAUSE_FAILURES.has(paused.error)
      )
        paused = await pause(null);
      const absenceBy = Date.now() + ABSENCE_WAIT_MS;
      while (
        paused.status === 'failed' &&
        paused.error === 'editor_changed' &&
        Date.now() < absenceBy
      ) {
        await sleep(ABSENCE_RETRY_MS);
        paused = await pause(null);
      }
      if (paused.status !== 'completed')
        throw new ApiRequestError(
          409,
          paused.error ?? 'workspace_stop_failed',
          'Your Workbench workspace could not be paused.',
        );
      return { released: true };
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: workspaceQuery.queryKey });
      void client.invalidateQueries({ queryKey: ['field-workspace'] });
    },
  });
}
